import {RefObject, useCallback, useEffect, useRef, useState} from 'react';
import * as Sentry from '@sentry/react';
import {useLivenessStore} from '../store/useLivenessStore';
import {WebcamRefTypes} from 'components/Webcam';
import {useEvent} from 'hooks';

const MaxRecordingTimeMS = 15_000;

// Function to get the best supported MIME type
const getSupportedMimeType = () => {
  // Detect Safari
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Safari-specific types (avoid webm as it's poorly supported)
  const safariTypes = [
    '', // Let Safari choose first
    'video/mp4',
  ];

  // Other browsers
  const generalTypes = [
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4;codecs=h264',
    'video/webm;codecs=vp9',
    'video/mp4',
    '', // Empty string as fallback - lets the browser choose
  ];

  const types = isSafari ? safariTypes : generalTypes;

  for (const type of types) {
    if (type === '' || MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }

  return ''; // Let the browser pick the format if none supported
};

type UseVideoRecordingProps = {
  videoRef?: RefObject<WebcamRefTypes>;
  onTimeExceeded?: () => void;
};

export const useVideoRecording = ({videoRef, onTimeExceeded}: UseVideoRecordingProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleTimeExceeded = useEvent(onTimeExceeded);
  const setRecordingVideo = useLivenessStore(state => state.setRecordingVideo);
  const recordingStartTime = useLivenessStore(state => state.recordingStartTime);
  const setRecordingStartTime = useLivenessStore(state => state.setRecordingStartTime);
  const incrementAttempts = useLivenessStore(state => state.incrementAttempts);

  const startRecording = useCallback(() => {
    if (!videoRef?.current?.video?.srcObject) return;

    recordedChunksRef.current = [];

    const stream = videoRef?.current?.video?.srcObject as MediaStream;

    const mimeType = getSupportedMimeType();

    const options = mimeType ? {mimeType} : undefined;

    const createMediaRecorder = (stream: MediaStream, options?: MediaRecorderOptions) => {
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Use the same MIME type for blob creation as was used for recording
        const blobType = mimeType || 'video/webm';
        const recordedBlob = new Blob(recordedChunksRef.current, {
          type: blobType,
        });
        setRecordingVideo(recordedBlob);
        recordedChunksRef.current = [];
      };

      return mediaRecorder;
    };

    try {
      const mediaRecorder = createMediaRecorder(stream, options);
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingStartTime(Date.now());
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error starting MediaRecorder with options:', error);
      try {
        const mediaRecorder = createMediaRecorder(stream);
        mediaRecorder.start();
        setIsRecording(true);
        setRecordingStartTime(Date.now());
      } catch (fallbackError) {
        console.error('MediaRecorder fallback error:', fallbackError);
      }
    }
  }, [videoRef, setRecordingVideo, setRecordingStartTime]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingStartTime(null);
    }
  }, [setRecordingStartTime]);

  useEffect(() => {
    if (!isRecording || !recordingStartTime) return;

    const checkTimeout = () => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - recordingStartTime;

      if (elapsedTime >= MaxRecordingTimeMS) {
        stopRecording();
        incrementAttempts();

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          startRecording();
          handleTimeExceeded();
          timeoutRef.current = null;
        }, 100);
      }
    };

    const timeoutInterval = setInterval(checkTimeout, 1000);

    return () => {
      clearInterval(timeoutInterval);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [
    isRecording,
    recordingStartTime,
    stopRecording,
    incrementAttempts,
    handleTimeExceeded,
    startRecording,
  ]);

  return {
    isRecording,
    startRecording,
    stopRecording,
  };
};
